import React from 'react';
import { Box, Typography, Tabs, Tab } from '@mui/material';
import { breakdownMesos, type MesoActivity } from '../lib/activityCalendar';
import ActivityCalendar from './ActivityCalendar';

interface ActivityBreakdownProps {
  activityCalendar: MesoActivity[];
}

// Planned vs completed workouts, per meso, behind the activity summary percentages
const ActivityBreakdown: React.FC<ActivityBreakdownProps> = ({ activityCalendar }) => {
  const mesos = React.useMemo(() => breakdownMesos(activityCalendar), [activityCalendar]);
  const [selected, setSelected] = React.useState<string | null>(null);

  if (mesos.length === 0) return null;

  // Newest meso until the runner picks one; also covers a runner switch that
  // leaves a label from the previous runner selected.
  const current = mesos.find(m => m.meso.meso === selected) ?? mesos[0];

  return (
    <Box sx={{
      width: '100%',
      mt: 3,
      bgcolor: 'background.paper',
      borderRadius: 3,
      p: { xs: 2.5, sm: 3 },
      boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
      border: '1px solid rgba(0, 0, 0, 0.05)',
    }}>
      <Box sx={{ mb: { xs: 1.5, sm: 2 } }}>
        <Typography
          variant="h6"
          sx={{
            fontSize: { xs: '1.1rem', sm: '1.25rem' },
            fontWeight: 700,
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            mb: 0.5
          }}
        >
          Planned vs Completed
        </Typography>
        <Typography variant="body2" sx={{ color: '#666', fontSize: { xs: '0.75rem', sm: '0.8125rem' } }}>
          How your mileage and strength percentages add up, day by day
        </Typography>
      </Box>

      {mesos.length > 1 && (
        <Tabs
          value={current.meso.meso}
          onChange={(_, value: string) => setSelected(value)}
          variant="scrollable"
          scrollButtons="auto"
          aria-label="Mesocycle"
          sx={{
            mb: 2,
            minHeight: 36,
            '& .MuiTab-root': { minHeight: 36, textTransform: 'none', fontWeight: 600 },
            '& .Mui-selected': { color: '#764ba2 !important' },
            '& .MuiTabs-indicator': { background: 'linear-gradient(135deg, #667eea, #764ba2)' },
          }}
        >
          {mesos.map(({ meso }) => (
            <Tab key={meso.meso} value={meso.meso} label={meso.meso} />
          ))}
        </Tabs>
      )}
      {mesos.length === 1 && (
        <Typography sx={{ fontWeight: 700, color: '#764ba2', mb: 1.5 }}>{current.meso.meso}</Typography>
      )}

      <ActivityCalendar key={current.meso.meso} calendar={current.calendar} official={current.meso.official} />
    </Box>
  );
};

export default ActivityBreakdown;
